/**
 * Barcode scanner → OpenFoodFacts lookup → add to the daily log.
 * Ports the flow from the Swift app's NutritionScanView (AVFoundation scanner +
 * NutritionLookupService). On the web we use the native BarcodeDetector +
 * getUserMedia camera preview where available (Android/Chrome), and fall back
 * to manual barcode entry (iOS Safari, no BarcodeDetector). Same EAN/UPC formats.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ScanLine, Check, Minus, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { MEAL_SLOT_KEYS, withDefaults } from '@/lib/meals';

interface Product {
  code: string;
  product_name: string;
  brands: string;
  serving_size: string | null;
  nutriments: {
    'energy-kcal_100g': number;
    proteins_100g: number;
    carbohydrates_100g: number;
    fat_100g: number;
  };
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];

// The native BarcodeDetector is not in TS's lib; declare what we use.
type BD = { detect: (src: CanvasImageSource) => Promise<Array<{ rawValue: string }>> };
declare global {
  interface Window {
    BarcodeDetector?: new (opts?: { formats?: string[] }) => BD;
  }
}

export default function BarcodeScan() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);

  const [supported] = useState(() => typeof window !== 'undefined' && 'BarcodeDetector' in window);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [looking, setLooking] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [product, setProduct] = useState<Product | null>(null);
  const [servings, setServings] = useState(1);
  const [labels, setLabels] = useState<Record<string, string>>(withDefaults(undefined));
  const [slot, setSlot] = useState<string>('meal_1');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ mealLabels: Record<string, string> }>('/api/nutrition/meal-labels')
      .then((d) => setLabels(withDefaults(d.mealLabels)))
      .catch(() => {});
  }, []);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
  }, []);

  const lookup = useCallback(async (code: string) => {
    setLooking(true);
    setNotFound(false);
    try {
      const data = await api.get<{ product: Product }>(`/api/nutrition/barcode/${code}`);
      setProduct(data.product);
      stopCamera();
    } catch {
      setNotFound(true);
    } finally {
      setLooking(false);
    }
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    if (!supported) return;
    setNotFound(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      const detector = new window.BarcodeDetector!({ formats: FORMATS });
      const tick = async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length && codes[0].rawValue) {
            await lookup(codes[0].rawValue.replace(/\D/g, ''));
            return;
          }
        } catch {
          /* keep trying */
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      setScanning(false);
    }
  }, [supported, lookup]);

  useEffect(() => {
    if (supported) startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addToLog() {
    if (!product) return;
    setSaving(true);
    const n = product.nutriments;
    const mult = servings; // OFF macros are per 100g; servings scales the 100g unit.
    try {
      await api.post('/api/nutrition/entries', {
        meal_slot: slot,
        food_name: `${product.product_name}${product.brands ? ` (${product.brands})` : ''}`,
        calories: Math.round((n['energy-kcal_100g'] || 0) * mult),
        protein_g: Math.round((n.proteins_100g || 0) * mult),
        carbs_g: Math.round((n.carbohydrates_100g || 0) * mult),
        fat_g: Math.round((n.fat_100g || 0) * mult),
        serving_size: 100 * mult,
        serving_unit: 'g',
        barcode: product.code,
      });
      navigate('/nutrition');
    } catch {
      setSaving(false);
    }
  }

  // ── Result view ──
  if (product) {
    const n = product.nutriments;
    const scaled = (v: number) => Math.round((v || 0) * servings);
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => { setProduct(null); if (supported) startCamera(); }} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
            <ArrowLeft size={22} />
          </button>
          <h1 className="display" style={{ fontSize: '1.7rem' }}>Confirm Food</h1>
        </div>

        <div className="card mb-4">
          <p className="font-semibold" style={{ color: 'var(--cream)' }}>{product.product_name}</p>
          {product.brands && <p className="text-sm" style={{ color: 'var(--cream-muted)' }}>{product.brands}</p>}
          <div className="flex gap-4 mt-3 text-sm" style={{ color: 'var(--cream-dim)' }}>
            <span><strong style={{ color: 'var(--cream)' }}>{scaled(n['energy-kcal_100g'])}</strong> cal</span>
            <span>P {scaled(n.proteins_100g)}g</span>
            <span>C {scaled(n.carbohydrates_100g)}g</span>
            <span>F {scaled(n.fat_100g)}g</span>
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--cream-muted)' }}>per {100 * servings}g</p>
        </div>

        {/* Servings */}
        <div className="card mb-4">
          <label className="label">Servings (100g each)</label>
          <div className="flex items-center gap-4">
            <button onClick={() => setServings((s) => Math.max(0.5, Math.round((s - 0.5) * 10) / 10))} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--surface)', color: 'var(--cream)' }}><Minus size={16} /></button>
            <span className="text-xl font-semibold" style={{ color: 'var(--cream)', minWidth: 40, textAlign: 'center' }}>{servings}</span>
            <button onClick={() => setServings((s) => Math.round((s + 0.5) * 10) / 10)} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--surface)', color: 'var(--cream)' }}><Plus size={16} /></button>
          </div>
        </div>

        {/* Meal slot */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5 no-scrollbar">
          {MEAL_SLOT_KEYS.map((key) => (
            <button key={key} onClick={() => setSlot(key)} className="whitespace-nowrap flex-shrink-0 px-3 py-2 text-xs font-medium transition-all" style={{ borderRadius: 'var(--radius-sm)', background: slot === key ? 'var(--champagne)' : 'var(--surface)', color: slot === key ? 'var(--ink)' : 'var(--cream-muted)' }}>{labels[key]}</button>
          ))}
        </div>

        <button onClick={addToLog} disabled={saving} className="btn-gold w-full flex items-center justify-center gap-2">
          <Check size={16} /> {saving ? 'Adding…' : 'Add to Log'}
        </button>
      </div>
    );
  }

  // ── Scanner / manual view ──
  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => { stopCamera(); navigate(-1); }} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>Scan Barcode</h1>
      </div>

      {supported ? (
        <div className="mb-5">
          <div style={{ position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden', aspectRatio: '4 / 3', background: 'var(--ink-2)', border: '1px solid var(--ink-line, rgba(244,239,227,0.08))' }}>
            <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div aria-hidden style={{ position: 'absolute', inset: '22% 14%', border: '2px solid var(--champagne)', borderRadius: 8, boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)' }} />
          </div>
          <p className="text-sm text-center mt-3 flex items-center justify-center gap-2" style={{ color: 'var(--cream-muted)' }}>
            <ScanLine size={14} /> {looking ? 'Looking up…' : scanning ? 'Point camera at a barcode' : 'Starting camera…'}
          </p>
        </div>
      ) : (
        <p className="text-sm mb-4" style={{ color: 'var(--cream-muted)' }}>
          Live scanning isn’t available in this browser. Type the barcode number below.
        </p>
      )}

      {/* Manual entry — always available as a fallback */}
      <form onSubmit={(e) => { e.preventDefault(); if (manualCode.trim()) lookup(manualCode.replace(/\D/g, '')); }}>
        <label className="label">Enter barcode number</label>
        <div className="flex gap-2">
          <input className="input flex-1" inputMode="numeric" placeholder="e.g. 737628064502" value={manualCode} onChange={(e) => setManualCode(e.target.value)} />
          <button type="submit" className="btn-gold px-4" disabled={looking || !manualCode.trim()}>Look up</button>
        </div>
      </form>

      {notFound && (
        <p className="text-sm mt-4 text-center" style={{ color: 'var(--warning)' }}>
          Product not found. Try again, or use Search Food.
        </p>
      )}
    </div>
  );
}
